

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { backendApi } from '@/lib/api/backend'
import type { components } from '@/types/generated/organizations'
import type { Role } from '@/config/dashboard-menu'

// Use CID-generated Organization type, extended with the org-scoped role
// returned by GET /v1/organizations (Phase 1 RBAC §1.2: dashboard reads
// the caller's current org role from the membership record). The role is
// optional because not every Organization payload carries it (the
// per-id GET response is bare Organization).
export type Organization = components['schemas']['Organization'] & {
  role?: Role
}

// Use CID-generated request types
export type CreateOrganizationData = components['schemas']['CreateOrganizationRequest']
export type UpdateOrganizationData = components['schemas']['UpdateOrganizationRequest']

// Type for the management API key returned on org creation
export interface CreatedManagementApiKey {
  id?: string
  keyPrefix?: string
  key?: string
}

// Type for the create organization result
export interface CreateOrganizationResult {
  organization: Organization
  managementApiKey?: CreatedManagementApiKey
}

interface OrganizationsStore {
  organizations: Organization[]
  currentOrg: Organization | null
  isLoading: boolean
  error: string | null
  _hasHydrated: boolean

  // Actions
  setOrganizations: (orgs: Organization[]) => void
  switchOrg: (orgId: string) => void
  setCurrentOrg: (org: Organization | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setHasHydrated: (hasHydrated: boolean) => void
  createOrganization: (data: CreateOrganizationData) => Promise<CreateOrganizationResult | null>
  updateOrganization: (orgId: string, data: UpdateOrganizationData) => Promise<boolean>
  deleteOrganization: (orgId: string) => Promise<boolean>
  fetchOrganizationDetails: (orgId: string) => Promise<Organization | null>
}

export const useOrganizations = create<OrganizationsStore>()(
  persist(
    (set, get) => ({
      organizations: [],
      currentOrg: null,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setOrganizations: (orgs) => set({ organizations: orgs }),

      switchOrg: (orgId) => {
        const org = get().organizations.find((o) => o.id === orgId)
        if (org) {
          set({ currentOrg: org })
          // Store last used org
          if (typeof window !== 'undefined') {
            localStorage.setItem('lastOrganizationId', orgId)
          }
        }
      },

      setCurrentOrg: (org) => set({ currentOrg: org }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),

      createOrganization: async (data) => {
        set({ isLoading: true, error: null })
        try {
          const response = await backendApi.organizations.create(data)
          const newOrg = response.organization

          set((state) => ({
            organizations: [...state.organizations, newOrg],
            isLoading: false,
          }))

          // Return full response including management API key if present
          return {
            organization: newOrg,
            managementApiKey: response.managementApiKey,
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create organization',
            isLoading: false,
          })
          return null
        }
      },

      updateOrganization: async (orgId, data) => {
        set({ isLoading: true, error: null })
        try {
          const response = await backendApi.organizations.update(orgId, data)
          const updatedOrg = response.organization

          set((state) => ({
            organizations: state.organizations.map((org) =>
              org.id === orgId ? updatedOrg : org
            ),
            currentOrg:
              state.currentOrg?.id === orgId
                ? updatedOrg
                : state.currentOrg,
            isLoading: false,
          }))

          return true
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update organization',
            isLoading: false,
          })
          return false
        }
      },

      deleteOrganization: async (orgId) => {
        set({ isLoading: true, error: null })
        try {
          await backendApi.organizations.delete(orgId)

          set((state) => ({
            organizations: state.organizations.filter((org) => org.id !== orgId),
            currentOrg: state.currentOrg?.id === orgId ? null : state.currentOrg,
            isLoading: false,
          }))

          return true
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete organization',
            isLoading: false,
          })
          return false
        }
      },

      fetchOrganizationDetails: async (orgId) => {
        set({ isLoading: true, error: null })
        try {
          const org = await backendApi.organizations.get(orgId)

          // Update the organization in the list if it exists
          set((state) => ({
            organizations: state.organizations.map((o) =>
              o.id === orgId ? org : o
            ),
            isLoading: false,
          }))

          return org
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch organization details',
            isLoading: false,
          })
          return null
        }
      },
    }),
    {
      name: 'organizations-storage',
      partialize: (state) => ({
        currentOrg: state.currentOrg,
        organizations: state.organizations,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
